/**
 * LaunchOrchestrationDialog — confirmation avant de lancer les passes MÉTIER.
 *
 * Chunk 9.AA.1b. Pourquoi une confirmation explicite : chaque passe fait analyser
 * le document par le fournisseur d'IA. Décision PO — « montrer les passes à lancer
 * avant confirmation », et **aucune UI monétaire** : on nomme le travail engagé,
 * jamais un montant.
 *
 * Patron d'accessibilité repris de `SendQuoteRequestDialog` (9.L.6), lui-même
 * aligné sur `ResolveEventDialog` (9.G.2) : `role="dialog"` + `aria-modal` +
 * `aria-labelledby` + `aria-describedby`, focus initial sur Annuler, Échap et clic
 * sur le fond pour fermer — tous neutralisés pendant l'envoi pour qu'un clic
 * malheureux ne laisse pas l'utilisateur dans le doute sur ce qui a été lancé.
 *
 * Le focus part sur **Annuler** et non sur Lancer : l'action est payante et
 * irréversible côté fournisseur. La touche Entrée ne doit pas la déclencher par
 * réflexe.
 */

import { useEffect, useId, useRef } from "react";
import { useTranslation } from "react-i18next";

export type LaunchOrchestrationDialogProps = {
  /** Passes qui vont réellement tourner, dans l'ordre du contrat. */
  passes: readonly string[];
  pending: boolean;
  /** Message d'erreur déjà traduit, ou `null`. */
  errorText: string | null;
  onConfirm: () => void;
  onClose: () => void;
};

export function LaunchOrchestrationDialog(props: LaunchOrchestrationDialogProps) {
  const { passes, pending, errorText, onConfirm, onClose } = props;
  const { t } = useTranslation();
  const titleId = useId();
  const descId = useId();
  const cancelRef = useRef<HTMLButtonElement | null>(null);

  useEffect(() => {
    queueMicrotask(() => cancelRef.current?.focus());
  }, []);

  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (e.key === "Escape" && !pending) onClose();
    }
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [pending, onClose]);

  const base = "submissions.detail.orchestration";
  // Le pluriel est choisi par i18next, pas ici. La sélection manuelle qui
  // occupait ces trois lignes dupliquait ce que `count` fait déjà — et elle
  // pointait `confirm_title_plural`, un suffixe retiré d'i18next en v21, donc
  // une clé qui aurait fini par s'afficher brute à l'écran.
  const titre = t(`${base}.confirm_title`, { count: passes.length });

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby={titleId}
      aria-describedby={descId}
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
    >
      <div
        aria-hidden="true"
        className="absolute inset-0 bg-slate-900/40 backdrop-blur-sm"
        onClick={() => {
          if (!pending) onClose();
        }}
      />
      <div className="motion-safe:animate-fade-in relative z-10 w-full max-w-md overscroll-contain rounded-xl bg-white shadow-xl ring-1 ring-slate-200">
        <header className="border-b border-slate-100 px-5 py-4">
          <h2 id={titleId} className="text-base font-semibold text-slate-900">
            {titre}
          </h2>
        </header>

        <div className="space-y-4 px-5 py-5">
          <p id={descId} className="text-sm text-slate-700">
            {t(`${base}.confirm_body`)}
          </p>

          <div>
            <p className="mb-2 text-xs font-medium uppercase tracking-wide text-slate-500">
              {t(`${base}.confirm_list_label`)}
            </p>
            <ul className="space-y-1">
              {passes.map((p) => (
                <li key={p} className="flex items-baseline gap-2 text-sm text-slate-800">
                  <span aria-hidden="true" className="text-slate-300">
                    •
                  </span>
                  <span className="min-w-0 break-words">
                    {t(`${base}.pass_label.${p}`, { defaultValue: p })}
                  </span>
                </li>
              ))}
            </ul>
          </div>

          {errorText && (
            <p
              aria-live="polite"
              className="rounded-md border border-red-200 bg-red-50 p-2 text-sm text-red-700"
            >
              {errorText}
            </p>
          )}
        </div>

        <footer className="flex justify-end gap-2 border-t border-slate-100 px-5 py-4">
          <button
            ref={cancelRef}
            type="button"
            onClick={onClose}
            disabled={pending}
            className="focus-visible:ring-brand-500 touch-manipulation rounded-md px-3 py-2 text-sm font-medium text-slate-700 hover:bg-slate-100 focus-visible:outline-none focus-visible:ring-2 disabled:opacity-50"
          >
            {t(`${base}.cancel`)}
          </button>
          <button
            type="button"
            onClick={onConfirm}
            disabled={pending}
            className="btn-primary focus-visible:ring-brand-500 touch-manipulation text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2"
          >
            {pending ? t(`${base}.launching`) : t(`${base}.confirm_cta`)}
          </button>
        </footer>
      </div>
    </div>
  );
}
